/**
 * The queue: the button on every grid card, the two answers a press on a card offers once a list is
 * running, and what the corner says when the list is over. The list itself is drawn by the watch
 * page's side panel, and every word on that panel is in `watch.queue`.
 *
 * **Nothing here calls a queue a playlist**, and that is the section's one rule. A playlist is saved
 * on the instance and comes back tomorrow; a queue belongs to this browser tab and is gone when the
 * tab closes. Every sentence a reader can act on says which of the two they are looking at, because
 * the difference is the only thing about a queue somebody has to learn.
 *
 * Which is why several sentences below do say "playlist": the list a card's button edits really can be
 * either one. A `list` in the address makes the queue a window on that playlist, so the press goes to
 * the playlist and the line has to name the list it changed.
 */
export const queue = {
  /** The button on a grid card's corner, and the same act wherever else it appears. */
  add: {
    /** The accessible name names the video; the hover word is the act alone (`WatchLaterButton`). */
    label: (title: string): string => `Add “${title}” to the queue`,
    title: 'Add to queue',
    /** The same button's other face, on a video the queue already holds. */
    remove: (title: string): string => `Remove “${title}” from the queue`,
    removeTitle: 'Remove from queue',
    removed: 'Removed from the queue.',
    /**
     * What a press said, for the ~2.5 s a `role="status"` line stays up. Several outcomes rather
     * than one, because "added" is the least useful of them: a reader who has just built a queue
     * wants to know it started, and one adding to a queue already playing wants to know where it went.
     */
    started: 'The queue is playing.',
    startedPaused: 'The queue is ready. Press play to start it.',
    /**
     * The press landed on the video that is already playing, so there is no "press play" to offer
     * and nothing was interrupted. Saying which video the list holds is the whole point of the line.
     */
    startedWithPlaying: 'The queue now holds the video that is playing.',
    addedNext: 'Added as the next video.',
    addedEnd: 'Added to the end of the queue.',
    /**
     * The same two presses while the list is a window on a playlist, where they change the playlist
     * itself. The add says the **end of the playlist** because that is where the video went, and a
     * reader whose list is only read as far as entry 40 of 500 will see no new row for it.
     */
    addedPlaylistEnd: 'Added to the end of the playlist.',
    removedPlaylist: 'Removed from the playlist.',
    /**
     * A press on this button could not fail while the list was only this tab's. On a playlist it can,
     * so the write's own sentence leads (`playlists/model.ts`) and what this adds is the one thing true
     * of every refusal: the list is as it was. The refusal has to lead rather than follow, because those
     * sentences are already about the list — "That entry is no longer there" after "the playlist still
     * holds the video" would have said both that the video is in the list and that it is not.
     *
     * Two entries reading the same words, deliberately: they are two acts, and the next change to one of
     * them should not have to separate them again first.
     */
    addRefused: (reason: string): string => `${reason}. The playlist was not changed.`,
    removeRefused: (reason: string): string => `${reason}. The playlist was not changed.`,
    /** Both faces while the change is on its way to the playlist. This tab's own list never waits. */
    working: 'Changing the playlist…',
  },

  /**
   * The two buttons that take over a card's picture when a press lands on it while a queue exists.
   * They are told apart by what happens to the list: one ends it, the other extends it. The card's
   * own Add button offers neither, because it has one meaning, the end of the list.
   *
   * Each button shows its short name and carries the sentence in its `title`, so the consequence is
   * one hover away and the picture stays readable underneath.
   */
  press: {
    now: 'Play now',
    nowLabel: (title: string): string => `Play “${title}” now and clear the queue`,
    nowTitle: 'The queue is cleared and this video opens.',
    next: 'Play next',
    nextLabel: (title: string): string => `Play “${title}” after the video that is playing`,
    nextTitle: 'This video plays directly after the one playing. This page stays open.',
    /**
     * The same button while the list is a playlist, where it is drawn and disabled. The reader can see
     * that it cannot be pressed, so what it owes them is the reason.
     */
    nextUnavailable: 'This list is a playlist, and a playlist cannot take a video next.',
    nextUnavailableLabel: (title: string): string =>
      `“${title}” cannot play next, because this list is a playlist.`,
  },

  /**
   * The corner box that takes the player's place once the queue has played its last video
   * (`playback/QueueEnded.tsx`). Two sentences, and the second one is the one that matters: nothing
   * has been thrown away, so the step back on this box leads into the list again.
   */
  ended: {
    line: 'The queue has ended. The list is still in this tab.',
    /**
     * The ✕, which empties the queue outright. It says what it does to the list rather than what it
     * does to the box, because the list is the part that cannot be pressed back.
     */
    close: 'Clear the queue',
  },
};
